/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { AlertTriangle, ShieldAlert, CheckCircle2, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const SEVERITY_META = {
  critical: { label: 'Critica', tone: 'border-red-500/60 bg-red-500/10 text-red-800 dark:text-red-200', Icon: ShieldAlert },
  major:    { label: 'Maggiore', tone: 'border-amber-500/60 bg-amber-500/10 text-amber-800 dark:text-amber-200', Icon: AlertTriangle },
  moderate: { label: 'Moderata', tone: 'border-yellow-500/60 bg-yellow-500/10 text-yellow-800 dark:text-yellow-200', Icon: Info },
  minor:    { label: 'Lieve', tone: 'border-muted bg-muted/40 text-foreground', Icon: Info },
};

/**
 * Compact panel that lists drug-drug interactions returned by the backend.
 * When `findings` is empty and `evaluated=true`, it shows a green "nessuna
 * interazione" chip. When `evaluated=false`, it displays nothing (form is
 * still building the list).
 */
export const InteractionAlerts = ({ findings = [], counts, currentTherapy = [], evaluated = false, loading = false }) => {
  if (loading) {
    return (
      <div className="text-xs text-muted-foreground italic" data-testid="interactions-loading">
        Verifica interazioni in corso…
      </div>
    );
  }
  if (!evaluated) return null;

  if (!findings || findings.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-xs text-emerald-800 dark:text-emerald-200" data-testid="interactions-clear">
        <CheckCircle2 className="h-4 w-4" />
        <span>Nessuna interazione clinicamente rilevante rilevata.</span>
        {currentTherapy?.length > 0 && (
          <span className="ml-1 text-[10.5px] opacity-75">(confronto con {currentTherapy.length} farmac{currentTherapy.length === 1 ? 'o' : 'i'} in terapia)</span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="interactions-alerts">
      <div className="flex flex-wrap items-center gap-1.5">
        <ShieldAlert className="h-4 w-4 text-red-600" />
        <span className="font-semibold text-sm">Interazioni rilevate</span>
        {(['critical','major','moderate','minor']).map((sev) => {
          const n = counts?.[sev] || 0;
          if (!n) return null;
          return <Badge key={sev} variant="outline" className={`text-[10px] ${SEVERITY_META[sev]?.tone || ''}`}>{n} {SEVERITY_META[sev]?.label || sev}</Badge>;
        })}
      </div>
      <ul className="space-y-1.5">
        {findings.map((f, i) => {
          const meta = SEVERITY_META[f.severity] || SEVERITY_META.minor;
          const Icon = meta.Icon;
          return (
            <li key={`${f.drug_a || ''}-${f.drug_b || ''}-${f.severity || ''}-${i}`} className={cn('flex items-start gap-2 rounded-md border p-2 text-xs', meta.tone)} data-testid={`interaction-${i}`}>
              <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-semibold">
                  {f.drug_a} <span className="opacity-60">×</span> {f.drug_b}
                  <Badge variant="outline" className="ml-1.5 text-[9px] uppercase">{meta.label}</Badge>
                </div>
                <div className="opacity-90">{f.reason}</div>
              </div>
            </li>
          );
        })}
      </ul>
      {currentTherapy?.length > 0 && (
        <div className="text-[10.5px] text-muted-foreground">
          Terapia in corso considerata: {currentTherapy.join(', ')}
        </div>
      )}
    </div>
  );
};

export default InteractionAlerts;
